import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { submitQuestion } from '@/lib/myAccount';
import type { FaqItem } from '@/types/product';

interface ProductFAQProps {
  sku: string;
  faqs: FaqItem[];
}

export default function ProductFAQ({ sku, faqs }: ProductFAQProps) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [question, setQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !question.trim()) return;
    setSubmitting(true);
    try {
      await submitQuestion(currentUser.id, 'Product', question.trim(), sku);
      showToast(t('products.questionSubmitted'));
      setQuestion('');
    } catch {
      showToast(t('products.questionSubmitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6]">
      <h3 className="text-sm tracking-[0.12em] uppercase font-medium text-[#2A2A2A] mb-6">
        {t('products.productFAQ')}
      </h3>

      <div className="flex flex-col gap-3 mb-6">
        {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return (
            <div key={i} className="border border-[#F0EFED] rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenIndex(isOpen ? null : i)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
              >
                <span className="text-sm font-medium text-[#2A2A2A]">{faq.question}</span>
                <i
                  className={`ri-arrow-down-s-line text-lg text-[#8A8A8A] transition-transform flex-shrink-0 ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {isOpen && (
                <div className="px-5 pb-4">
                  <p className="text-xs text-[#8A8A8A] font-light leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Ask a question */}
      <div className="p-5 rounded-xl bg-[#FAFAF9] border border-[#F0EFED]">
        <h4 className="text-xs font-medium text-[#2A2A2A] mb-3">{t('products.askQuestion')}</h4>
        {!currentUser ? (
          <Link to="/login" className="text-xs text-[#7B1113] hover:underline">
            {t('products.loginToAsk')}
          </Link>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('products.questionPlaceholder')}
              rows={2}
              required
              className="w-full px-3 py-2 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] resize-none"
            />
            <button
              type="submit"
              disabled={submitting || !question.trim()}
              className="self-end px-6 py-2.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors disabled:opacity-50 cursor-pointer"
            >
              {t('products.submitQuestion')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
